import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
  Alert,
  CircularProgress,
  Link,
  Typography
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { loginAsync, clearError } from "../../../store/slices/authSlice";
import ForgotPasswordForm from "./ForgotPasswordForm";

/**
 * Formulário de login em modal.
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Se o modal está aberto
 * @param {Function} props.onClose - Callback para fechar modal
 * @returns {JSX.Element}
 */
function LoginForm({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const { isLoading, error, isAuthenticated } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !isClosing) {
      setIsClosing(true);
      // Usar setTimeout para evitar problemas de foco e reflow
      setTimeout(() => {
        onClose();
        setFormData({ email: '', password: '' });
        setErrors({});
        setIsClosing(false);
      }, 100);
    }
  }, [isAuthenticated, isClosing, onClose]);

  useEffect(() => {
    if (open) {
      dispatch(clearError());
    }
  }, [open, dispatch]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email é obrigatório';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email inválido';
    if (!formData.password) newErrors.password = 'Senha é obrigatória';
    else if (formData.password.length < 6) newErrors.password = 'Senha deve ter pelo menos 6 caracteres';
    return newErrors;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    dispatch(loginAsync(formData));
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Login
        <IconButton onClick={onClose} size="small">
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            name="email"
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            disabled={isLoading}
            autoComplete="email"
          />
          <TextField
            name="password"
            label="Senha"
            type="password"
            fullWidth
            margin="normal"
            value={formData.password}
            onChange={handleChange}
            error={!!errors.password}
            helperText={errors.password}
            disabled={isLoading}
            autoComplete="current-password"
          />
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button 
              type="submit" 
              variant="contained" 
              fullWidth
              disabled={isLoading || isClosing}
              startIcon={isLoading && <CircularProgress size={20} />}
            >
              {isLoading ? 'Entrando...' : 'Entrar'}
            </Button>
            <Button onClick={onClose} variant="outlined" fullWidth disabled={isLoading || isClosing}>
              Cancelar
            </Button>
          </Box>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link 
              component="button"
              type="button"
              variant="body2" 
              onClick={() => setForgotPasswordOpen(true)}
              sx={{ cursor: 'pointer' }}
            >
              Esqueci minha senha
            </Link>
          </Box>
        </Box>
      </DialogContent>
      
      <ForgotPasswordForm 
        open={forgotPasswordOpen} 
        onClose={() => setForgotPasswordOpen(false)} 
      />
    </Dialog>
  );
}

export default LoginForm;